import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Container,
  Paper,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';

interface ConnectPageConfig {
  serviceName: string;
  serviceDisplayName: string;
  apiBaseUrl: string;
  routePath: string;
  backendOAuthPath?: string;
  storageKeys: {
    email: string;
    returnUrl: string;
    clientId: string;
    state: string;
    redirectUri: string;
    codeChallenge: string;
  };
  messages: {
    requestAccess: string;
    directAccessError: string;
    connectDescription: string;
    connectingMessage: string;
    successMessage: string;
    errorMessage: string;
    buttonText: string;
  };
}

interface ConnectPageProps {
  config: ConnectPageConfig;
}

const ConnectPage: React.FC<ConnectPageProps> = ({ config }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMcpFlow, setIsMcpFlow] = useState(false);

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Check for OAuth callback parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const connected = urlParams.get('connected');
    const message = urlParams.get('message');
    const redirect = urlParams.get('redirect');
    const returnUrl = urlParams.get('return_url');

    // Check if this is a Claude MCP flow
    if (redirect === 'mcp') {
      const clientId = urlParams.get('client_id');
      const state = urlParams.get('state');
      const redirectUri = urlParams.get('redirect_uri');
      const codeChallenge = urlParams.get('code_challenge');
      const sessionId = urlParams.get('session_id');

      console.log('MCP Redirect detected:', { clientId, state, redirectUri, codeChallenge, sessionId, returnUrl });

      setIsMcpFlow(true);
      setNotification({
        open: true,
        message: config.messages.requestAccess,
        severity: 'info'
      });

      // Store OAuth parameters for reference
      if (returnUrl) localStorage.setItem(config.storageKeys.returnUrl, returnUrl);
      if (clientId) localStorage.setItem(config.storageKeys.clientId, clientId);
      if (state) localStorage.setItem(config.storageKeys.state, state);
      if (redirectUri) localStorage.setItem(config.storageKeys.redirectUri, redirectUri);
      if (codeChallenge) localStorage.setItem(config.storageKeys.codeChallenge, codeChallenge);

    } else if (!status && window.location.pathname === config.routePath) {
      // No MCP redirect and no status - this means direct access without Claude
      setNotification({
        open: true,
        message: config.messages.directAccessError,
        severity: 'error'
      });
      return;
    }

    if (status === 'success' && connected === 'true') {
      const email = urlParams.get('email');
      if (email) {
        setUserEmail(email);
        localStorage.setItem(config.storageKeys.email, email);
      }

      setIsConnected(true);

      // Check if this was from an MCP redirect
      const mcpReturnUrl = localStorage.getItem(config.storageKeys.returnUrl);
      const mcpClientId = localStorage.getItem(config.storageKeys.clientId);
      const mcpState = localStorage.getItem(config.storageKeys.state);

      console.log('Checking MCP completion:', { mcpReturnUrl, mcpClientId, mcpState });

      if (mcpReturnUrl && mcpClientId) {
        console.log('MCP OAuth completion triggered');

        setNotification({
          open: true,
          message: config.messages.successMessage + ' Redirecting back to Claude...',
          severity: 'success'
        });

        setTimeout(() => {
          // Complete the OAuth callback for Claude - use different endpoints for different services
          const callbackPath = config.backendOAuthPath === '/oauth/meta' ? '/meta/callback' : '/mcp/callback';
          const callbackUrl = `${config.apiBaseUrl}${callbackPath}?code=auth_success&state=${mcpState}&client_id=${mcpClientId}`;
          console.log('Redirecting to MCP callback:', callbackUrl);

          // Redirect to complete OAuth flow
          window.location.href = callbackUrl;
        }, 2000);

        // Clean up stored values
        localStorage.removeItem(config.storageKeys.returnUrl);
        localStorage.removeItem(config.storageKeys.clientId);
        localStorage.removeItem(config.storageKeys.state);
        localStorage.removeItem(config.storageKeys.redirectUri);
        localStorage.removeItem(config.storageKeys.codeChallenge);
      } else {
        setNotification({
          open: true,
          message: config.messages.successMessage + ' You can now close this page and return to Claude.',
          severity: 'success'
        });
      }

      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === 'error') {
      setNotification({
        open: true,
        message: message || config.messages.errorMessage,
        severity: 'error'
      });

      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [config]);

  const handleConnect = async () => {
    // Check if this is for MCP integration
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const clientId = urlParams.get('client_id');
    const redirect = urlParams.get('redirect');

    // Only allow Claude MCP flow
    if (redirect !== 'mcp' || !sessionId || !clientId) {
      setNotification({
        open: true,
        message: config.messages.directAccessError,
        severity: 'error'
      });
      return;
    }

    setIsConnecting(true);

    try {
      // MCP OAuth flow - redirect to appropriate OAuth endpoint based on service
      const oauthPath = config.backendOAuthPath || '/oauth/google';
      window.location.href = `${config.apiBaseUrl}${oauthPath}?client_id=${clientId}&session_id=${sessionId}`;
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to initiate connection. Please try again.',
        severity: 'error'
      });
      setIsConnecting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: 'rgba(20, 20, 20, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
          }}
        >
          {/* PlexHq Branding */}
          <Box mb={6}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center',
                mb: 1,
                letterSpacing: '-0.02em',
                textShadow: '0 0 30px rgba(102, 126, 234, 0.3)',
              }}
            >
              Plex HQ
            </Typography>
            <Box
              sx={{
                width: '100px',
                height: '3px',
                background: 'linear-gradient(90deg, #667eea, #764ba2)',
                margin: '0 auto',
                borderRadius: '2px',
                mb: 4,
              }}
            />
          </Box>

          <Box mb={4}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 300,
                color: 'white',
                mb: 2,
              }}
            >
              {config.serviceDisplayName}
            </Typography>
            <Typography
              variant="h4"
              component="h3"
              gutterBottom
              sx={{
                fontWeight: 500,
                background: 'linear-gradient(45deg, #4285f4, #34a853)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3,
              }}
            >
              Claude Integration
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                mb: 4,
                fontSize: '1.1rem',
              }}
            >
              {isConnected
                ? config.serviceName + ' account connected successfully! ' + (isMcpFlow ? 'Redirecting back to Claude...' : 'You can now close this page and return to Claude.')
                : isMcpFlow
                  ? config.messages.connectDescription
                  : config.messages.directAccessError
              }
            </Typography>
          </Box>

          {isMcpFlow && !isConnected && (
            <Button
              variant="contained"
              size="large"
              startIcon={
                isConnecting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <GoogleIcon />
                )
              }
              onClick={handleConnect}
              disabled={isConnecting}
              sx={{
                py: 2,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 500,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #4285f4, #34a853)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #3367d6, #2d9142)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(66, 133, 244, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(66, 133, 244, 0.3)',
                },
                transition: 'all 0.3s ease',
                textTransform: 'none',
              }}
            >
              {isConnecting ? config.messages.connectingMessage : config.messages.buttonText}
            </Button>
          )}

          {isConnected && (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(76, 175, 80, 0.8)',
                  fontSize: '1.1rem',
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                ✓ {isMcpFlow ? 'Redirecting to Claude...' : 'Connection complete!'}
              </Typography>
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 3,
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            Secure OAuth 2.0 authentication powered by Google
          </Typography>
        </Paper>
      </Container>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConnectPage;
